/**
 * Word plumbing for the `style-checkers-a` tools (comma splices, to-be verbs, vague
 * pronouns, Latin abbreviations, number style, dialogue punctuation).
 *
 * The shared writing-analysis tokenisers split at hyphens and were written for English
 * word lists. These checkers also read apostrophes and accents, so a word here is a run
 * of letters, combining marks or digits with internal straight or curly apostrophes
 * ("don’t", "O’Brien", "café", "naïve" are one word each). Offsets always point into the
 * text the reader pasted.
 */

export interface Word {
  /** As written. */
  text: string;
  /** Lowercase, with curly apostrophes straightened. */
  lower: string;
  start: number;
  end: number;
}

const RE_WORD = /[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}]+)*/gu;
const RE_CURLY = /’/g;
const RE_UPPER_START = /^\p{Lu}/u;
const RE_FIRST_LETTER = /\p{L}/u;
const RE_OPEN_PAREN = /[([]/g;
const RE_CLOSE_PAREN = /[)\]]/g;

export function lowerWord(text: string): string {
  return text.toLowerCase().replace(RE_CURLY, "'");
}

/** The words of `text` between `from` and `to`, with offsets into `text`. */
export function wordsIn(text: string, from = 0, to = text.length): Word[] {
  const words: Word[] = [];
  for (const match of text.slice(from, to).matchAll(RE_WORD)) {
    const start = from + match.index;
    words.push({
      text: match[0],
      lower: lowerWord(match[0]),
      start,
      end: start + match[0].length,
    });
  }
  return words;
}

export function startsUpper(text: string): boolean {
  return RE_UPPER_START.test(text);
}

/** Uppercase the first letter, leaving the rest as written. */
export function capitalizeFirst(text: string): string {
  const letter = RE_FIRST_LETTER.exec(text);
  if (!letter) {
    return text;
  }
  const at = letter.index;
  return text.slice(0, at) + letter[0].toUpperCase() + text.slice(at + 1);
}

/** Lowercase the first letter. */
export function lowercaseFirst(text: string): string {
  const letter = RE_FIRST_LETTER.exec(text);
  if (!letter) {
    return text;
  }
  const at = letter.index;
  return text.slice(0, at) + letter[0].toLowerCase() + text.slice(at + 1);
}

/** Open brackets minus closed ones in `text.slice(from, to)`; above 0 means "inside". */
export function bracketDepth(text: string, from: number, to: number): number {
  const slice = text.slice(from, to);
  const opens = slice.match(RE_OPEN_PAREN)?.length ?? 0;
  const closes = slice.match(RE_CLOSE_PAREN)?.length ?? 0;
  return opens - closes;
}

/** Collapse runs of whitespace (line breaks included) to single spaces. */
const RE_WHITESPACE_RUN = /\s+/g;
export function singleSpaced(text: string): string {
  return text.replace(RE_WHITESPACE_RUN, " ").trim();
}

/** A short clause-sized excerpt of `text` for tables: collapse whitespace, cap length. */
export function excerpt(text: string, max = 140): string {
  const flat = singleSpaced(text);
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`;
}
